import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import CustomButton from '../../../components/CustomButton';
import NativeSelect from '../../../components/ui/NativeSelect';
import { useGetProductsQuery } from '../../../api/group/groupAdminApiSlice';
import { useTenantCapabilities } from '../../../hooks/useTenantCapabilities';
import {
    productsByUseCase,
    stationLinkableProducts,
} from '../createStation/productUseCases';
import {
    invalidateInventoryReaders,
    useRelinkObjectMutation,
} from '../../../api/inventory/inventoryApiSlice';
import { readInventoryFailure } from '../../../api/inventory/inventoryErrors';
import { type ObjectActionAbortReason, readObjectActionFailure } from '../objectActionOutcome';
import type { StationObject } from '../../../api/station/stationApiTypes';

interface Props {
    /** The objects the operator ticked — each must carry a `linkId`. */
    selected: StationObject[];
    onDone: () => void;
}

/** One compartment that would not move, and the line the operator reads. */
interface PositionFailure {
    id: string;
    text: string;
}

/**
 * Move the ticked compartments to a different Product.
 *
 * ── The gap this closes ────────────────────────────────────────────────────
 *
 * Ten compartments were created as size L and five of them were meant to be M.
 * Adding objects worked; **correcting** them did not — not because anything was
 * missing underneath, but because nothing could reach it. `PATCH
 * /inventory/product-links/:linkId` has existed all along, with its blocker
 * guards and its two-model transaction, and so has the `useRelinkObjectMutation`
 * hook. What was missing was the link id in the station payload and a control
 * (PO, KGSt-Forum 2026-09-12).
 *
 * ── Why one call per compartment, and why that is not a loop bug ───────────
 *
 * The endpoint re-links ONE link, and each one is independently refusable: the
 * backend checks per object whether a rental is running on it or a booking is
 * committed to it, and answers 409 for that object alone. A bulk endpoint would
 * have to decide all-or-nothing, and all-or-nothing is the wrong answer here —
 * an operator correcting nine compartments should not be blocked because the
 * tenth is in use right now. So the calls are sequential and the outcome is
 * reported per position: what moved, and which compartments could not and why.
 *
 * Sequential rather than parallel on purpose: each re-link runs a transaction
 * that also calls `ensureInterimGroup` for the target product, and firing ten
 * of those at once would race to create the same interim row.
 */
const ChangeProductBar = ({ selected, onDone }: Props) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { data } = useGetProductsQuery();
    const { services } = useTenantCapabilities();
    const [relinkObject] = useRelinkObjectMutation();

    const [productId, setProductId] = useState('');
    const [blocked, setBlocked] = useState<PositionFailure[]>([]);
    const [movedCount, setMovedCount] = useState<number | null>(null);
    const [aborted, setAborted] = useState<ObjectActionAbortReason | null>(null);
    /*
     * Our own in-flight flag, not the mutation hook's `isLoading`.
     *
     * The run is N sequential calls, and between one resolving and the next
     * dispatching `isLoading` is false — a window in which a second click would
     * start a second run over the same compartments. This flag spans the whole
     * run, which is the thing that must not be started twice.
     */
    const [running, setRunning] = useState(false);

    const products = useMemo(
        () => stationLinkableProducts(data ?? [], services),
        [data, services],
    );
    const groupedProducts = useMemo(() => productsByUseCase(products), [products]);

    const labelForUseCase = (useCase: string): string =>
        t(`service_type_selector.${useCase.toLowerCase()}`, { defaultValue: useCase });

    /** Ticked compartments that are actually re-linkable. */
    const linkable = useMemo(
        () => selected.filter((object) => !!object.linkId),
        [selected],
    );
    // An object that fulfils no product has no link to move. It is ticked and
    // visible, so say so rather than silently doing nothing with it.
    const unlinkedCount = selected.length - linkable.length;

    const handleApply = async () => {
        if (!productId.trim() || linkable.length === 0 || running) return;
        setRunning(true);
        setBlocked([]);
        setMovedCount(null);
        setAborted(null);

        // Keyed by object id, not by the rendered line: two objects with no door
        // mapping can carry the same name, and a duplicate React key on the one
        // list the operator has to read is not worth the shortcut.
        const failures: PositionFailure[] = [];
        let moved = 0;
        let abort: ObjectActionAbortReason | null = null;

        try {
            for (const object of linkable) {
                const position = object.door ?? object.name ?? object.id;
                try {
                    const result = await relinkObject({
                        linkId: object.linkId as string,
                        productId: productId.trim(),
                    }).unwrap();
                    if (result.changed) moved += 1;
                } catch (error) {
                    const read = readObjectActionFailure(readInventoryFailure(error));
                    // Nothing was decided about this compartment or about any of
                    // the rest — the service is down, or we are not allowed.
                    // Firing the remaining calls would produce one identical
                    // error line per compartment, which reads as a data problem
                    // and sends the operator to the bookings instead of to the
                    // backend.
                    if (read.abort) {
                        abort = read.abort;
                        break;
                    }
                    // Object-scoped wording, not the shared `station_detail.blockers.*`
                    // set: those are phrased for a station-wide refusal ("a rental
                    // is running at this station"), and here the backend counted
                    // live holders and future bookings of THIS asset alone. The
                    // shared string would report the whole station as busy when
                    // nine of ten compartments are free to move.
                    const reason = read.reasonKeys.length
                        ? read.reasonKeys
                            .map((key) => t(`station_detail.objects.${key}`, {
                                defaultValue: t('station_detail.blockers.unknown'),
                            }))
                            .join(' ')
                        : [t('station_detail.objects.change_product_error'), read.detail]
                            .filter(Boolean)
                            .join(' ');
                    failures.push({ id: object.id, text: `${position}: ${reason}` });
                }
            }
        } finally {
            setRunning(false);
        }

        invalidateInventoryReaders(dispatch);
        setBlocked(failures);
        setMovedCount(moved);
        setAborted(abort);
        // Keep the bar open when something was refused, so the operator can read
        // WHICH compartment and why. Clearing the selection on a partial failure
        // would hide exactly the part that needs a decision.
        if (failures.length === 0 && !abort) onDone();
    };

    return (
        <div className={'rounded-[5px] border border-foreground/15 bg-foreground/5 px-4 py-3'}>
            <div className={'flex flex-wrap items-end gap-3'}>
                <p className={'text-sm font-medium text-foreground'}>
                    {t('station_detail.objects.selected_count', { selected: selected.length })}
                </p>

                <div className={'flex flex-col gap-1'}>
                    <label
                        htmlFor={'station-change-product'}
                        className={'text-xs text-foreground/60'}
                    >
                        {t('station_detail.objects.change_product_label')}
                    </label>
                    <NativeSelect
                        id={'station-change-product'}
                        value={productId}
                        onChange={(e) => setProductId(e.target.value)}
                    >
                        <option value={''}>
                            {t('station_create.link.product_placeholder')}
                        </option>
                        {groupedProducts.map(([useCase, items]) => (
                            <optgroup key={useCase} label={labelForUseCase(useCase)}>
                                {items.map((product) => (
                                    <option key={product.id} value={product.id}>
                                        {product.name ?? product.id}
                                    </option>
                                ))}
                            </optgroup>
                        ))}
                    </NativeSelect>
                </div>

                <CustomButton
                    title={t('station_detail.objects.change_product_apply')}
                    onClick={handleApply}
                    disabled={!productId.trim() || linkable.length === 0 || running}
                    loading={running}
                />
                <CustomButton
                    title={t('common.cancel')}
                    isFilled={false}
                    onClick={onDone}
                />
            </div>

            {unlinkedCount > 0 && (
                <p className={'mt-2 text-sm text-foreground/60'}>
                    {t('station_detail.objects.change_product_unlinked', {
                        unlinked: unlinkedCount,
                    })}
                </p>
            )}

            {/* One line for the whole run, not one per compartment: the run
                stopped because nothing answered, which is a fact about the
                backend and says nothing about any compartment. */}
            {aborted && (
                <p className={'mt-2 text-sm text-red-500'}>
                    {t(`station_detail.objects.change_product_abort_${aborted}`)}
                </p>
            )}

            {movedCount !== null && movedCount > 0 && (
                <p className={'mt-2 text-sm text-foreground'}>
                    {t('station_detail.objects.change_product_moved', { moved: movedCount })}
                </p>
            )}

            {/* Named per compartment, not as one "some failed": which door is in
                use right now is precisely what the operator has to act on. */}
            {blocked.length > 0 && (
                <div className={'mt-2'}>
                    <p className={'text-sm text-red-500'}>
                        {t('station_detail.objects.change_product_blocked')}
                    </p>
                    <ul className={'mt-1 list-disc pl-5 text-sm text-red-500'}>
                        {blocked.map((entry) => <li key={entry.id}>{entry.text}</li>)}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default ChangeProductBar;
